import {
    Component,
    computed,
    inject,
    OnInit,
    signal,
    OnDestroy,
    HostListener,
    effect,
    viewChild,
    ElementRef,
    AfterViewInit,
} from '@angular/core'
import { Router } from '@angular/router'
import { OnboardingService } from 'src/app/core/services/onboarding/onboarding.service'
import { OnboardingCategory, OnboardingItem } from '../../interfaces/onboarding/onboarding-item.interface'
import { ButtonComponent } from '../form-elements/button/button.component'

@Component({
    selector: 'sf-onboarding-checklist',
    imports: [ButtonComponent],
    templateUrl: './onboarding-checklist.component.html',
    styleUrl: './onboarding-checklist.component.scss',
})
export class OnboardingChecklistComponent implements OnInit, AfterViewInit, OnDestroy {
    private onboardingService = inject(OnboardingService)
    private router = inject(Router)

    checklistElement = viewChild<ElementRef<HTMLDivElement>>('checklistElement')

    items = this.onboardingService.getItems()
    progress = computed(() => this.onboardingService.getProgress())
    isMinimized = signal(false)
    activeCategory = signal<OnboardingCategory | null>(null)
    isVisible = signal(true)
    isDismissed = computed(() => this.onboardingService.isOnboardingDismissed())

    // Drag functionality
    isDragging = false
    dragPosition = signal({ x: 0, y: 0 }) // Will be calculated on init
    dragOffset = { x: 0, y: 0 }

    // Constants for positioning
    private readonly CHECKLIST_WIDTH = 420
    private readonly PAGE_ACTION_BAR_HEIGHT = 80 // Approximate height of page action bar
    private readonly MARGIN = 20
    private readonly MIN_SCREEN_WIDTH = 1200

    OnboardingCategory = OnboardingCategory

    constructor() {
        // Watch for when the checklist is reopened and reset position
        effect(() => {
            const dismissed = this.isDismissed()
            if (!dismissed) {
                // Reset position to bottom right when reopened
                setTimeout(() => this.setInitialPosition(), 0)
            }
        })
    }

    ngOnInit(): void {
        this.checkScreenSize()
    }

    ngAfterViewInit(): void {
        // Set initial position after view is initialized and we can get actual height
        this.setInitialPosition()
    }

    ngOnDestroy(): void {
        // Cleanup if dragging
        if (this.isDragging) {
            document.removeEventListener('mousemove', this.onDrag.bind(this))
            document.removeEventListener('mouseup', this.onDragEnd.bind(this))
        }
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.checkScreenSize()
        this.constrainPosition()
    }

    private checkScreenSize(): void {
        this.isVisible.set(window.innerWidth >= this.MIN_SCREEN_WIDTH)
    }

    private getChecklistHeight(): number {
        return this.checklistElement()?.nativeElement?.offsetHeight || 600
    }

    private constrainPosition(): void {
        const currentPos = this.dragPosition()
        const maxX = window.innerWidth - this.CHECKLIST_WIDTH
        const checklistHeight = this.getChecklistHeight()
        const maxY = window.innerHeight - checklistHeight

        this.dragPosition.set({
            x: Math.max(0, Math.min(currentPos.x, maxX)),
            y: Math.max(0, Math.min(currentPos.y, maxY)),
        })
    }

    private setInitialPosition(): void {
        // Position at bottom right corner
        const checklistHeight = this.getChecklistHeight()
        const x = window.innerWidth - this.CHECKLIST_WIDTH - this.MARGIN
        const y = window.innerHeight - checklistHeight - this.PAGE_ACTION_BAR_HEIGHT - this.MARGIN

        this.dragPosition.set({
            x: Math.max(this.MARGIN, x),
            y: Math.max(this.MARGIN, y),
        })
    }

    private moveToBottomRight(): void {
        // Calculate bottom right position
        const checklistHeight = this.getChecklistHeight()
        const x = window.innerWidth - this.CHECKLIST_WIDTH - this.MARGIN
        const y = window.innerHeight - checklistHeight - this.PAGE_ACTION_BAR_HEIGHT - this.MARGIN

        this.dragPosition.set({
            x: Math.max(this.MARGIN, x),
            y: Math.max(this.MARGIN, y),
        })
    }

    categories = computed(() => {
        const items = this.items()
        return Object.values(OnboardingCategory).map((category) => ({
            name: category,
            items: items.filter((item) => item.category === category),
            completed: items.filter((item) => item.category === category).every((item) => item.completed),
            progress: this.getCategoryProgress(category),
        }))
    })

    getCategoryProgress(category: OnboardingCategory): number {
        const categoryItems = this.items().filter((item) => item.category === category)
        const completed = categoryItems.filter((item) => item.completed).length
        return categoryItems.length > 0 ? Math.round((completed / categoryItems.length) * 100) : 0
    }

    getCompletedCount(items: OnboardingItem[]): number {
        return items.filter((item) => item.completed).length
    }

    toggleMinimize(): void {
        this.isMinimized.update((v) => !v)
        if (this.isMinimized()) {
            this.moveToBottomRight()
        }
    }

    toggleCategory(category: OnboardingCategory): void {
        this.activeCategory.update((current) => (current === category ? null : category))
    }

    toggleItem(item: OnboardingItem): void {
        this.onboardingService.toggleCompletion(item.id)
    }

    navigateToItem(item: OnboardingItem): void {
        if (item.route) {
            this.router.navigate([item.route])
            this.onboardingService.markAsComplete(item.id)
        } else if (item.action) {
            item.action()
        }
    }

    dismiss(): void {
        // Move to bottom right before dismissing
        this.moveToBottomRight()
        // Use setTimeout to allow animation to complete before dismissing
        setTimeout(() => {
            this.onboardingService.dismissOnboarding()
        }, 300)
    }

    // Drag functionality methods
    onDragStart(event: MouseEvent): void {
        this.isDragging = true
        const rect = (event.currentTarget as HTMLElement).parentElement?.getBoundingClientRect()
        if (rect) {
            this.dragOffset.x = event.clientX - rect.left
            this.dragOffset.y = event.clientY - rect.top
        }

        document.addEventListener('mousemove', this.onDrag.bind(this))
        document.addEventListener('mouseup', this.onDragEnd.bind(this))

        event.preventDefault()
    }

    onDrag(event: MouseEvent): void {
        if (!this.isDragging) return

        const newX = event.clientX - this.dragOffset.x
        const newY = event.clientY - this.dragOffset.y

        // Constrain to viewport - prevent moving off the page
        const checklistHeight = this.getChecklistHeight()
        const maxX = window.innerWidth - this.CHECKLIST_WIDTH
        const maxY = window.innerHeight - checklistHeight

        this.dragPosition.set({
            x: Math.max(0, Math.min(newX, maxX)),
            y: Math.max(0, Math.min(newY, maxY)),
        })
    }

    onDragEnd(): void {
        this.isDragging = false
        document.removeEventListener('mousemove', this.onDrag.bind(this))
        document.removeEventListener('mouseup', this.onDragEnd.bind(this))
    }
}
